/*
 * One-off status lines at the top of the dashboard (Req 21: inline, never a
 * viewport toast):
 *  - arriving from /start as a returner ("back") or without a free event
 *    ("none"); a new account's welcome is the 6A first run's own line;
 *  - back from Stripe Checkout (`/channels?checkout=success&plan=<plan>`):
 *    "Finishing up…" while the session catches up with the webhook, the plan
 *    line once `paid_at` lands, and a pending line (never a false success)
 *    if it is slow.
 */
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { ft, Alert, Spinner } from "components/FreeEvent";
import { selectUser } from "containers/App/selectors";
import { fetchSession } from "containers/App/actions";
import "containers/FreeStart/free-start.css";

const FAST_MS = 2000;
const FAST_FOR_MS = 10000;
const SLOW_MS = 10000;
const POLL_FOR_MS = 3 * 60000;
const PLANS = ["lite", "plus", "pro"];
const selectUserState = selectUser();

// "You're in, Nia." when we know the name, "You're in." when we don't.
const named = (key, firstName) => (firstName ? ft(key, { first_name: firstName }) : ft(`${key}0`));

export const ArrivalNote = ({ outcome, firstName }) => {
  if (outcome === "back") {
    return (
      <div className="mb-fs note">
        <Alert kind="ok" icon={CheckCircleIcon} title={named("to.back.b", firstName)}>
          {ft("to.back")}
        </Alert>
      </div>
    );
  }
  if (outcome === "none") {
    return (
      <div className="mb-fs note">
        <Alert kind="info" title={named("to.new.b", firstName)}>
          {ft("to.none")}
        </Alert>
      </div>
    );
  }
  return null;
};
export const PaidNote = ({ plan, phase }) => {
  if (phase === "finishing") {
    return (
      <div className="mb-fs note">
        <div className="alert info" role="status">
          <Spinner />
          <div>
            <b>{ft("pl.finishing")}</b>
          </div>
        </div>
      </div>
    );
  }
  // The webhook hasn't reached the session yet: never claim the plan is on.
  if (phase === "pending") {
    return (
      <div className="mb-fs note">
        <Alert kind="info" title={ft("pl.pending.b")}>
          {ft("pl.pending")}
        </Alert>
      </div>
    );
  }
  const known = PLANS.includes(plan);
  return (
    <div className="mb-fs note">
      <Alert kind="ok" icon={CheckCircleIcon} title={known ? ft("to.paid.b", { plan_name: ft(`pl.name.${plan}`) }) : null}>
        {ft(plan === "lite" ? "to.paid.lite" : "to.paid")}
      </Alert>
    </div>
  );
};

// Fast while the webhook usually lands, then slower, then stop (the pending
// line says to refresh).
const nextPollMs = (elapsed) => (elapsed < FAST_FOR_MS ? FAST_MS : elapsed < POLL_FOR_MS ? SLOW_MS : null);

const FreeEventNotes = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUserState);
  const [arrival] = useState(() => {
    const s = location.state && location.state.freeStart;
    return s === "back" || s === "none" ? s : null;
  });
  const [firstName] = useState(() => location.state && location.state.firstName);
  const params = new URLSearchParams(location.search);
  const [checkout] = useState(() => (params.get("checkout") === "success" ? params.get("plan") || "" : null));
  const [elapsed, setElapsed] = useState(0);
  const started = useRef(Date.now());

  const paid = Boolean(user && user.free_event && user.free_event.paid_at);
  const settled = paid || Boolean(user && user.free_event === undefined && user.email);
  const phase = checkout === null ? null : settled ? "done" : elapsed < FAST_FOR_MS ? "finishing" : "pending";

  // Drop the one-off markers once they've done their job, so a reload or back
  // doesn't show them again. The checkout markers stay until the server
  // confirms the plan: the limited shell unlocks into the full app on that
  // confirmation, and the remounted note reads them to show the plan line.
  const done = arrival !== null || phase === "done";
  useEffect(() => {
    if (!done) return;
    const next = new URLSearchParams(location.search);
    if (!next.has("checkout") && !next.has("plan") && !location.state) return;
    next.delete("checkout");
    next.delete("plan");
    const search = next.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ""}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  useEffect(() => {
    if (checkout === null || settled) return undefined;
    const wait = nextPollMs(elapsed);
    if (wait === null) return undefined;
    const id = setTimeout(() => {
      dispatch(fetchSession());
      setElapsed(Date.now() - started.current);
    }, wait);
    return () => clearTimeout(id);
  }, [checkout, settled, elapsed, dispatch]);

  const name = firstName || (user && user.first_name) || undefined;
  if (checkout !== null) return <PaidNote plan={checkout} phase={phase} />;
  if (arrival === "back" || arrival === "none") return <ArrivalNote outcome={arrival} firstName={name} />;
  return null;
};

export default FreeEventNotes;
